export const validLogicalRange = (range) =>
  range &&
  Number.isFinite(Number(range.from)) &&
  Number.isFinite(Number(range.to)) &&
  Number(range.to) > Number(range.from);

export const normalizeLogicalRange = (range) =>
  validLogicalRange(range)
    ? { from: Number(range.from), to: Number(range.to) }
    : null;

export const normalizeTimeRange = (range) => {
  const from = Number(range?.from);
  const to = Number(range?.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
    return null;
  }
  return { from, to };
};

export const buildChartRangeIdentityKey = ({
  selectedTickerId,
  scale,
  assetType,
}) => [selectedTickerId ?? "", scale ?? "", assetType ?? ""].join("|");

export const timeRangeOverlapsSeries = ({ range, times }) => {
  const normalized = normalizeTimeRange(range);
  if (!normalized || !Array.isArray(times) || times.length === 0) return false;

  const first = Number(times[0]);
  const last = Number(times[times.length - 1]);
  if (!Number.isFinite(first) || !Number.isFinite(last)) return false;

  return normalized.from <= last && normalized.to >= first;
};

export const releaseRangeSyncSuppression = (suppressRangeSyncRef) => {
  if (!suppressRangeSyncRef) return;
  if (typeof window === "undefined" || !window.requestAnimationFrame) {
    suppressRangeSyncRef.current = false;
    return;
  }
  window.requestAnimationFrame(() => {
    suppressRangeSyncRef.current = false;
  });
};

export const scheduleRangeApply = ({
  chart,
  range,
  suppressRangeSyncRef,
}) => {
  if (!chart || !range) return;
  const timeScale = chart.timeScale();
  if (!timeScale?.setVisibleLogicalRange) return;

  suppressRangeSyncRef.current = true;
  timeScale.setVisibleLogicalRange(range);
  releaseRangeSyncSuppression(suppressRangeSyncRef);
};

export const shiftPinnedRangeForAppend = ({
  range,
  previousPointCount,
  nextPointCount,
}) => {
  const normalized = normalizeLogicalRange(range);
  if (!normalized) return null;

  if (
    !Number.isFinite(previousPointCount) ||
    !Number.isFinite(nextPointCount) ||
    previousPointCount <= 0 ||
    nextPointCount <= previousPointCount
  ) {
    return normalized;
  }

  const previousLastIndex = previousPointCount - 1;
  const appendedCount = nextPointCount - previousPointCount;
  // A pro-style chart is still pinned when its latest candle is visible with
  // empty logical space to the right. Our normal display ranges intentionally
  // include that right padding, so equality with the last index is too strict.
  const wasPinnedToLatest = normalized.to >= previousLastIndex - 0.05;

  if (!wasPinnedToLatest) return normalized;

  return {
    from: normalized.from + appendedCount,
    to: normalized.to + appendedCount,
  };
};

export const rebaseLogicalRangeForPointCount = ({
  range,
  previousPointCount,
  nextPointCount,
}) => {
  const normalized = normalizeLogicalRange(range);
  if (!normalized) return null;

  if (
    !Number.isFinite(previousPointCount) ||
    !Number.isFinite(nextPointCount) ||
    previousPointCount <= 0 ||
    nextPointCount <= 0
  ) {
    return normalized;
  }

  const span = normalized.to - normalized.from;
  const previousLastIndex = previousPointCount - 1;
  const nextLastIndex = nextPointCount - 1;
  const rightEdgeOffset = normalized.to - previousLastIndex;
  const to = nextLastIndex + rightEdgeOffset;

  return {
    from: to - span,
    to,
  };
};

export const rebaseLogicalRangeForSeriesSwitch = ({
  range,
  previousPointCount,
  nextPointCount,
}) => {
  const normalized = normalizeLogicalRange(range);
  if (!normalized) return null;

  const rebased = rebaseLogicalRangeForPointCount({
    range: normalized,
    previousPointCount,
    nextPointCount,
  });
  if (
    !rebased ||
    !Number.isFinite(Number(nextPointCount)) ||
    Number(nextPointCount) <= 0
  ) {
    return rebased;
  }

  const nextLastIndex = Number(nextPointCount) - 1;
  const overlapsNextSeries = rebased.to >= 0 && rebased.from <= nextLastIndex;
  if (overlapsNextSeries) return rebased;

  // A far-back range from a long history can sit entirely outside a shorter
  // replacement series. Preserve the zoom span, but anchor it to real data so
  // changing assets never presents an empty chart.
  const span = normalized.to - normalized.from;
  return {
    from: nextLastIndex - span,
    to: nextLastIndex,
  };
};

export const restoreLogicalRangeFromSharedSnapshot = ({
  snapshot,
  fallbackRange = null,
  nextPointCount,
  nextScale,
}) => {
  const snapshotRange = normalizeLogicalRange(snapshot?.range);
  if (!snapshotRange) return normalizeLogicalRange(fallbackRange);

  const rebase = snapshot?.scale === nextScale
    ? rebaseLogicalRangeForSeriesSwitch
    : rebaseLogicalRangeForPointCount;
  return rebase({
    range: snapshotRange,
    previousPointCount: snapshot?.pointCount,
    nextPointCount,
  });
};
